import * as React from 'react';
import { ThemeProvider as StyledComponentsThemeProvider } from 'styled-components';

import { useConfig } from './ConfigProvider';

const DEFAULT_THEME_COLOR = '#000000';

interface IThemeProviderProps {
  children: React.ReactNode;
  fullWidth?: boolean;
}

export interface IThemeContext {
  merchantColor: string;
  fullWidth: boolean;
  fullWidthAddAndDeclineProtection: boolean;
  promptCopyAndSvgColor: string;
}

type RGB = [number, number, number];

// TODO: why reinvent the wheel? values.js has helpers for this stuff
function hexToRGB(hex: string) {
  hex = hex.slice(1);
  const value = parseInt(hex, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;

  return [r, g, b] as RGB;
}

function luminance(rgb: RGB) {
  const [r, g, b] = rgb.map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return r * 0.2126 + g * 0.7152 + b * 0.0722;
}

function contrastRatio(fgColor: RGB, bgColor: RGB) {
  const fgLuminance = luminance(fgColor);
  const bgLuminance = luminance(bgColor);
  const contrast = bgLuminance < fgLuminance
    ? ((bgLuminance + 0.05) / (fgLuminance + 0.05))
    : ((fgLuminance + 0.05) / (bgLuminance + 0.05));
  return 1 / contrast;
}

export const getTextColor = (bgColor: string) => {
  const bg = hexToRGB(bgColor);
  const whiteContrast = contrastRatio([255, 255, 255], bg);
  const blackContrast = contrastRatio([0, 0, 0], bg);
  return (whiteContrast > blackContrast) ? '#ffffff' : '#000000';
};

const ThemeContext = React.createContext<IThemeContext>({
  merchantColor: DEFAULT_THEME_COLOR,
  fullWidth: false,
  fullWidthAddAndDeclineProtection: false,
  promptCopyAndSvgColor: '--black',
});

export function ThemeProvider(props: IThemeProviderProps) {
  const { children, fullWidth } = props;
  const config = useConfig();

  const value: IThemeContext = {
    merchantColor: config.color,
    fullWidth: !!fullWidth,
    fullWidthAddAndDeclineProtection: false, // 2023/02/06 slide-out-interstitial-cta experiment
    promptCopyAndSvgColor: '--black',
  };

  return (
    <ThemeContext.Provider value={value}>
      <StyledComponentsThemeProvider theme={value}>
        {children}
      </StyledComponentsThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return React.useContext(ThemeContext);
}

export const ThemeOverrideProvider = ({ children, ...features }: IThemeProviderProps & Partial<IThemeContext>) => {
  const inheritedFeatures = useTheme();
  return (
    <ThemeContext.Provider value={{ ...inheritedFeatures, ...features }}>
      <StyledComponentsThemeProvider theme={{ ...inheritedFeatures, ...features }}>
        {children}
      </StyledComponentsThemeProvider>
    </ThemeContext.Provider>
  );
};